import { combineReducers, createAction, createReducer, createSelector } from '@reduxjs/toolkit';
import { message } from 'common/services/message';

import api from 'common/api';
import { AppState } from 'common/models/AppState';
import { CompanyInfoFormValues } from 'common/models/Forms';
import { normalizePhoneFieldValues } from 'common/validation';
import { UserProfile } from 'common/models/User';

import { startListening } from '../../root/store/listener-middleware';
import { savePersistedAuth, clearPersistedAuth } from '../../root/store/persistence';
import login, { LoginState, loginActions } from './features/login/ducks';
import registration, { RegistrationState } from './features/registration/ducks';
import forgottenPassword, { ForgottenPasswordState } from './features/forgotten-password/ducks';

// Actions
export const logoutActions = {
  request: createAction('@@Auth/LOGOUT/REQUEST'),
  success: createAction('@@Auth/LOGOUT/SUCCESS'),
  failure: createAction('@@Auth/LOGOUT/FAILURE'),
};

export const updateProfileActions = {
  request: createAction<Partial<UserProfile>>('@@Auth/UpdateProfile/REQUEST'),
  success: createAction<UserProfile>('@@Auth/UpdateProfile/SUCCESS'),
  failure: createAction('@@Auth/UpdateProfile/FAILURE'),
};

export const checkUserActions = {
  request: createAction('@@Auth/CheckUser/REQUEST'),
  success: createAction('@@Auth/CheckUser/SUCCESS'),
  failure: createAction('@@Auth/CheckUser/FAILURE'),
};

// Reducers
export interface AuthState {
  login: LoginState;
  registration: RegistrationState;
  forgottenPassword: ForgottenPasswordState;
  profile: UserProfile | null;
  logged: boolean;
}

export const rehydrateAuthAction = createAction<AuthState>('@@Auth/REHYDRATE');

const profile = createReducer<AuthState['profile']>(null, (builder) => {
  builder
    .addCase(rehydrateAuthAction, (_state, action) => action.payload.profile)
    .addCase(loginActions.success, (_state, action) => action.payload)
    .addCase(updateProfileActions.success, (_state, action) => action.payload)
    .addCase(logoutActions.success, () => null);
});

const logged = createReducer(false, (builder) => {
  builder
    .addCase(rehydrateAuthAction, (_state, action) => action.payload.logged)
    .addCase(logoutActions.success, () => false)
    .addCase(loginActions.success, () => true);
});

export default combineReducers({
  login,
  profile,
  logged,
  registration,
  forgottenPassword,
});

// Selectors
export function selectAuth(state: AppState) {
  return state.auth;
}

export function selectIsLogged(state: AppState) {
  return selectAuth(state).logged;
}

export function selectUserProfile(state: AppState) {
  return selectAuth(state).profile;
}

export function selectIsPrivileged(state: AppState) {
  return selectAuth(state).profile?.roles?.privileged ?? false;
}

export const selectPartnerData = createSelector([selectUserProfile], (user): CompanyInfoFormValues | null =>
  user
    ? {
        address: `${user?.street ?? ''}, ${user?.city ?? ''}`,
        email: user?.email ?? '',
        firstName: user?.firstName ?? '',
        gender: user?.meta?.gender ?? '',
        ico: user?.meta?.ico ?? '',
        lastName: user?.lastName ?? '',
        partnerNumber: user?.meta?.idcard ?? '',
        phone: normalizePhoneFieldValues(user?.phone, user?.locale === 'cz' ? '420' : '421'),
        title: user?.title?.before ?? '',
      }
    : null,
);

// Listeners
startListening({
  actionCreator: updateProfileActions.request,
  effect: async (action, listenerApi) => {
    listenerApi.cancelActiveListeners();
    const resp = await api.user.updateProfile(action.payload);

    if (resp.ok) {
      listenerApi.dispatch(updateProfileActions.success(resp.data));
      message.success('Profile sucessfully changed');
    } else {
      listenerApi.dispatch(updateProfileActions.failure());
    }
  },
});

startListening({
  actionCreator: checkUserActions.request,
  effect: async (_action, listenerApi) => {
    listenerApi.cancelActiveListeners();
    const resp = await api.user.profile();

    if (resp.ok) {
      listenerApi.dispatch(loginActions.success(resp.data));
    } else {
      listenerApi.dispatch(logoutActions.success());
    }
  },
});

startListening({
  actionCreator: logoutActions.request,
  effect: async (_action, listenerApi) => {
    listenerApi.cancelActiveListeners();
    const resp = await api.user.logout();

    if (resp.ok) {
      listenerApi.dispatch(logoutActions.success());
    } else {
      listenerApi.dispatch(logoutActions.failure());
    }
  },
});

// Persistence listeners
startListening({
  predicate: (action) => loginActions.success.match(action) || updateProfileActions.success.match(action),
  effect: async (_action, listenerApi) => {
    const { auth } = listenerApi.getState();
    await savePersistedAuth(auth);
  },
});

startListening({
  actionCreator: logoutActions.success,
  effect: async () => {
    await clearPersistedAuth();
  },
});
